import * as THREE from 'three';
import { MINERAL_DEFS, WORLD, levelCurve, type MineralKind, type ChipId } from './config';
import { createMineralSprite, createToolSprite } from './pixelAssets';
import { stageForLevel } from './stages';
import type { SeededRandom } from '../utils/random';

export interface Mineral {
  id: number;
  kind: MineralKind;
  x: number;
  y: number;
  radius: number;
  weight: number;
  value: number;
  color: string;
  combo: string;
  active: boolean;
  group: THREE.Group;
  bobPhase: number;
  grabbedBy: number | null;
  hiddenBy: number | null;
  pickup?: ChipId;
}

export function createMineralMesh(kind: MineralKind): THREE.Group {
  const def = MINERAL_DEFS[kind];
  const group = new THREE.Group();
  group.add(createMineralSprite(kind, def.radius * 2.2));

  return group;
}

export function spawnMineral(
  id: number,
  kind: MineralKind,
  x: number,
  y: number,
  scene: THREE.Scene,
): Mineral {
  const def = MINERAL_DEFS[kind];
  const group = createMineralMesh(kind);
  group.position.set(x, y, 0);
  scene.add(group);
  return {
    id,
    kind,
    x,
    y,
    radius: def.radius,
    weight: def.weight,
    value: def.value,
    color: def.color,
    combo: def.combo,
    active: true,
    group,
    bobPhase: id * 0.7,
    grabbedBy: null,
    hiddenBy: null,
  };
}

export function disposeMineral(m: Mineral, scene: THREE.Scene): void {
  scene.remove(m.group);
  m.group.traverse((obj) => {
    if (obj instanceof THREE.Sprite) obj.material.dispose();
    if (obj instanceof THREE.Mesh) {
      obj.geometry.dispose();
      const mat = obj.material;
      if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
      else mat.dispose();
    }
  });
}

export function spawnToolPickup(id:number,tool:ChipId,x:number,y:number,scene:THREE.Scene):Mineral {
  const m=spawnMineral(id,'starDust',x,y,scene);
  m.group.traverse(o=>{if(o instanceof THREE.Sprite)o.material.dispose()});m.group.clear();
  m.group.add(createToolSprite(tool,1.35));
  const ring=new THREE.Mesh(new THREE.RingGeometry(.67,.73,16),new THREE.MeshBasicMaterial({color:'#d9b569',transparent:true,opacity:.8,depthWrite:false}));
  ring.position.z=-.1;m.group.add(ring);
  m.radius=.6;m.weight=.8;m.value=0;m.combo='none';m.pickup=tool;return m;
}

/** Stage-specific treasure pools; obstacles have a separate, explicit budget. */
const STAGE_TABLES: { kind: MineralKind; w: number }[][] = [
  [{kind:'amber',w:24},{kind:'goldBlock',w:15},{kind:'copper',w:24},{kind:'diamond',w:3}],
  [{kind:'fluorite',w:18},{kind:'pearl',w:12},{kind:'cyanCrystal',w:14},{kind:'sapphire',w:12},{kind:'diamond',w:7}],
  [{kind:'garnet',w:22},{kind:'fossil',w:10},{kind:'sunstone',w:16},{kind:'magentaCrystal',w:12},{kind:'goldBlock',w:12}],
  [{kind:'jade',w:22},{kind:'fossil',w:8},{kind:'obsidian',w:12},{kind:'relic',w:14},{kind:'sapphire',w:8}],
  [{kind:'moonstone',w:18},{kind:'starGeode',w:16},{kind:'sunstone',w:12},{kind:'relic',w:12},{kind:'diamond',w:8},{kind:'sapphire',w:12}],
];
function pickWeighted(table: {kind:MineralKind;w:number}[],rng:SeededRandom): MineralKind {
  let value=rng()*table.reduce((sum,row)=>sum+row.w,0);
  for(const row of table){value-=row.w;if(value<=0)return row.kind;}
  return table[0].kind;
}
export function rawTreasureValue(minerals: readonly Mineral[]): number {
  return minerals.reduce((sum,m)=>sum+(m.combo!=='none'?m.value:0),0);
}

export function generateLevel(level:number,rng:SeededRandom,scene:THREE.Scene,twoPlayer:boolean,resourceMultiplier=twoPlayer?1.75:1,hazardMultiplier=twoPlayer?1.25:1): Mineral[] {
  const stage=stageForLevel(level),curve=levelCurve(level);
  const budget=Math.floor(curve.target*curve.potentialRatio*resourceMultiplier);
  const table=[{kind:'goldNugget' as const,w:level<5?16:6},...STAGE_TABLES[stage.number-1]];
  const planned:MineralKind[]=[...stage.signature,...stage.signature];
  let value=planned.reduce((sum,kind)=>sum+MINERAL_DEFS[kind].value,0);
  // Budget both abundance and rarity; never inject unlimited valuable minerals.
  while(value<budget-45&&planned.length<80){
    const remaining=budget-value;
    const available=table.filter(row=>MINERAL_DEFS[row.kind].value<=remaining);
    if(!available.length)break;
    const kind=planned.length>=50?available.reduce((best,row)=>MINERAL_DEFS[row.kind].value>MINERAL_DEFS[best.kind].value?row:best).kind:pickWeighted(available,rng);planned.push(kind);value+=MINERAL_DEFS[kind].value;
  }
  const hazards=Math.ceil(curve.hazards*hazardMultiplier);
  const obstacles:MineralKind[]=Array.from({length:hazards},(_,i)=>i%4===3?'dynamite':i%4===2?'trash':'rock');
  const minerals:Mineral[]=[];
  const halfW=WORLD.halfWidth-1.2,bottom=WORLD.floorY+1.4;
  const place=(kind:MineralKind,obstacle:boolean):void=>{
    const radius=MINERAL_DEFS[kind].radius;
    const deep=!obstacle&&(rng()<curve.deepRatio||(level>=3&&MINERAL_DEFS[kind].value>=250));
    for(let attempt=0;attempt<1800;attempt++){
      const x=(rng()*2-1)*halfW;
      const y=deep&&attempt<1000 ? -2.5-rng()*(-2.5-bottom) : 3.3-rng()*(3.3-bottom);
      const reachable=(twoPlayer?[-4.2,4.2]:[0]).some(pivot=>
        Math.abs(Math.atan2(x-pivot,WORLD.pivotY-y))<WORLD.swingAmplitude-.04
        &&Math.hypot(x-pivot,WORLD.pivotY-y)<WORLD.maxHookLength-.4);
      if(!reachable||minerals.some(m=>Math.hypot(m.x-x,m.y-y)<m.radius+radius+.3))continue;
      minerals.push(spawnMineral(minerals.length+1,kind,x,y,scene));return;
    }
    throw new Error(`矿谷布局空间不足：第 ${level} 层 ${kind}`);
  };
  // Obstacles occupy actual space and shooting lanes before treasures are placed.
  for(const kind of obstacles)place(kind,true);
  for(const kind of planned)place(kind,false);
  return minerals;
}
